import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreServicesApi, type BotConfiguration } from '../lib/api/core-services.js';
import type { SupportedBotName } from '@discord-bot-platform/service-client';

// Query keys
export const botConfigKeys = {
  all: ['botConfigurations'] as const,
  lists: () => [...botConfigKeys.all, 'list'] as const,
  list: (botName: SupportedBotName) => [...botConfigKeys.lists(), botName] as const,
  details: () => [...botConfigKeys.all, 'detail'] as const,
  detail: (botName: SupportedBotName, configId: string) => [...botConfigKeys.details(), botName, configId] as const,
};

// Hook to get all configurations for a bot
export const useBotConfigurations = (botName: SupportedBotName) => {
  return useQuery({
    queryKey: botConfigKeys.list(botName),
    queryFn: async () => {
      const response = await coreServicesApi.llm.getBotConfig(botName);
      return response.configurations || [];
    },
    enabled: !!botName,
    staleTime: 30 * 60 * 1000, // 30 minutes - much longer to prevent refetches
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
};

// Hook to get specific configuration
export const useBotConfiguration = (botName: SupportedBotName, configId: string) => {
  return useQuery({
    queryKey: botConfigKeys.detail(botName, configId),
    queryFn: async () => {
      const response = await coreServicesApi.llm.getBotConfigById(botName, configId);
      return response.configuration;
    },
    enabled: !!botName && !!configId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to create a new bot configuration
export const useCreateBotConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ botName, config }: { botName: SupportedBotName; config: Partial<BotConfiguration> }) => {
      const response = await coreServicesApi.llm.createBotConfig(botName, config);
      return response.configuration;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch configurations for this bot
      queryClient.invalidateQueries({ queryKey: botConfigKeys.list(variables.botName) });
    },
  });
};

// Hook to update a bot configuration
export const useUpdateBotConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      botName, 
      configId, 
      config 
    }: { 
      botName: SupportedBotName; 
      configId: string; 
      config: Partial<BotConfiguration> 
    }) => {
      const response = await coreServicesApi.llm.updateBotConfig(botName, configId, config);
      return response.configuration;
    },
    onSuccess: (_, variables) => {
      // Invalidate specific configuration and list
      queryClient.invalidateQueries({ queryKey: botConfigKeys.detail(variables.botName, variables.configId) });
      queryClient.invalidateQueries({ queryKey: botConfigKeys.list(variables.botName) });
    },
  });
};

// Hook to delete a bot configuration
export const useDeleteBotConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ botName, configId }: { botName: SupportedBotName; configId: string }) => {
      await coreServicesApi.llm.deleteBotConfig(botName, configId);
    },
    onSuccess: (_, variables) => {
      // Remove from cache and refetch list
      queryClient.removeQueries({ queryKey: botConfigKeys.detail(variables.botName, variables.configId) });
      queryClient.invalidateQueries({ queryKey: botConfigKeys.list(variables.botName) });
    },
  });
};

// Hook to get configuration versions (for history)
export const useConfigurationVersions = (botName: SupportedBotName, configId: string) => {
  return useQuery({
    queryKey: [...botConfigKeys.detail(botName, configId), 'versions'],
    queryFn: async () => {
      return await coreServicesApi.llm.getConfigVersions(botName, configId);
    },
    enabled: !!botName && !!configId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook to find active configuration for a specific prompt type
export const useActiveBotConfiguration = (botName: SupportedBotName, promptType: string) => {
  const { data: configurations, ...query } = useBotConfigurations(botName);
  
  const activeConfig = configurations?.find(config => 
    config.promptType === promptType && config.isActive
  );

  return {
    ...query,
    data: activeConfig,
    configurations,
  };
};

// Utility hook for common bot configuration operations
export const useBotConfigurationOperations = (botName: SupportedBotName) => {
  const createMutation = useCreateBotConfiguration();
  const updateMutation = useUpdateBotConfiguration();
  const deleteMutation = useDeleteBotConfiguration();

  const saveConfiguration = async (config: Partial<BotConfiguration>, existingConfigId?: string) => {
    if (existingConfigId) {
      return await updateMutation.mutateAsync({
        botName,
        configId: existingConfigId,
        config,
      });
    } else {
      return await createMutation.mutateAsync({
        botName,
        config,
      });
    }
  };

  const deleteConfiguration = async (configId: string) => {
    return await deleteMutation.mutateAsync({ botName, configId });
  };

  return {
    saveConfiguration,
    deleteConfiguration,
    isSaving: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    error: createMutation.error || updateMutation.error || deleteMutation.error,
    isError: createMutation.isError || updateMutation.isError || deleteMutation.isError,
    isSuccess: createMutation.isSuccess || updateMutation.isSuccess || deleteMutation.isSuccess,
  };
};
